import { getCsrfToken } from "@/api/messages";
import type { FileLibraryDocumentChatBlockedCode } from "@/api/file_library_document_chat";

async function fileLibraryRequestError(response: Response, fallback: string): Promise<Error> {
  const data: unknown = await response.json().catch(() => ({}));
  const record = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const message =
    typeof record.error === "string" && record.error.trim().length > 0 ? record.error.trim() : fallback;
  return new Error(message);
}

export type FileLibraryDocument = {
  id: number;
  name: string;
  file_path: string | null;
  status: string;
  page_count?: number | null;
  original_filename?: string | null;
  file_extension?: string | null;
  display_filename?: string | null;
  file_kind?: string | null;
  file_kind_label?: string | null;
  file_icon?: string | null;
  downloadable?: boolean;
  download_url?: string | null;
  previewable?: boolean;
  preview_url?: string | null;
  document_chat_blocked_reason?: string | null;
  document_chat_blocked_code?: FileLibraryDocumentChatBlockedCode | null;
  // The folder this document belongs to for the current municipality (null = ungrouped). Present on
  // documents returned by the per-folder and search endpoints (ENG-1422 lazy loading).
  group_id?: number | null;
};

export type FileLibraryGroup = {
  id: number;
  name: string;
  description: string | null;
  position: number;
  parent_id?: number | null;
  document_ids: number[];
};

export type FileLibraryPayload = {
  documents: FileLibraryDocument[];
  groups: FileLibraryGroup[];
};

export type FileLibrarySearchPayload = {
  documents: FileLibraryDocument[];
  search: string;
  limit: number;
};

export type FileLibrarySummary = {
  document_count: number;
  folder_count: number;
};

export type SearchFileLibraryDocumentsParams = {
  search: string;
  limit?: number;
  excludeIds?: number[];
  signal?: AbortSignal;
};

// ENG-1539/1540: natural-language File Center search. Folder hits carry a breadcrumb `path`
// ("Ordinances > 02 Parking > Final") and a similarity `score` (0..1, or null for keyword-only
// matches). Document hits reuse the standard document payload plus the same nullable score.
export type FileLibraryFolderHit = {
  id: number;
  name: string;
  parent_id: number | null;
  path: string;
  description: string | null;
  score: number | null;
};

export type FileLibraryDocumentHit = FileLibraryDocument & {
  score: number | null;
};

export type FileLibrarySearchResult = {
  query: string;
  folders: FileLibraryFolderHit[];
  documents: FileLibraryDocumentHit[];
  total_match_count?: number;
  limited?: boolean;
  timed_out?: boolean;
  metadata?: FileLibraryPaginationMetadata;
  id?: number;
  status?: "pending" | "processing" | "completed" | "failed";
};

export type FileLibrarySearchMode = "intelligent" | "exact";

export type ExactSearchFolderChoice = {
  id: number;
  name: string;
};

export type ExactSearchComposeResult = {
  all: string[];
  phrase: string[];
  any: string[];
  none: string[];
  folders: ExactSearchFolderChoice[];
};

export type SearchFileLibraryParams = {
  q: string;
  folderLimit?: number;
  documentLimit?: number;
  page?: number;
  perPage?: number;
  signal?: AbortSignal;
};

// ENG-1422: lazy File Center loading. The folder tree (counts only) is loaded once; each folder's
// documents are fetched on demand and paginated.
export type FileLibraryFolder = {
  id: number;
  name: string;
  description: string | null;
  position: number;
  parent_id: number | null;
  document_count: number;
  subtree_document_count: number;
  has_children: boolean;
};

export type FileLibraryFolderTree = {
  folders: FileLibraryFolder[];
  ungrouped_count: number;
  total_count: number;
  chat_ready_count: number;
};

export type FileLibraryPaginationMetadata = {
  current_page: number;
  total_pages: number;
  per_page: number;
  total_count: number;
};

export type PaginatedFolderDocuments = {
  documents: FileLibraryDocument[];
  metadata: FileLibraryPaginationMetadata;
};

// A folder id, or the literal "ungrouped" for documents not in any folder.
export type FolderTarget = number | "ungrouped";

export type GetFolderDocumentsParams = {
  page?: number;
  perPage?: number;
  signal?: AbortSignal;
};

export async function searchFileLibraryDocuments(
  municipalityId: number,
  params: SearchFileLibraryDocumentsParams,
): Promise<FileLibrarySearchPayload> {
  const searchParams = new URLSearchParams();
  searchParams.set("search", params.search.trim());
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.excludeIds?.length) {
    searchParams.set("exclude_ids", params.excludeIds.join(","));
  }

  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/file_library?${searchParams.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      signal: params.signal,
    },
  );

  if (!response.ok) {
    throw await fileLibraryRequestError(response, `Failed to search file library (${response.status})`);
  }

  return response.json();
}

// Natural-language search across folders and documents for the File Center. The query is sent as
// typed (trimmed only, never lowercased) so semantic ranking sees the user's words. See ENG-1540.
export async function searchFileLibrary(
  municipalityId: number,
  params: SearchFileLibraryParams,
): Promise<FileLibrarySearchResult> {
  const searchParams = new URLSearchParams();
  searchParams.set("q", params.q.trim());
  if (params.folderLimit != null) searchParams.set("folder_limit", String(params.folderLimit));
  if (params.documentLimit != null) searchParams.set("document_limit", String(params.documentLimit));
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.perPage != null) searchParams.set("per_page", String(params.perPage));

  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/file_library/search?${searchParams.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      signal: params.signal,
    },
  );

  if (!response.ok) {
    throw await fileLibraryRequestError(response, `Failed to search file library (${response.status})`);
  }

  return response.json();
}

export async function startExactSearch(
  municipalityId: number,
  params: {
    q: string;
    folderLimit?: number;
    folderIds?: number[];
    includeUngrouped?: boolean;
    signal?: AbortSignal;
  },
): Promise<FileLibrarySearchResult> {
  const body: Record<string, unknown> = { q: params.q.trim() };
  if (params.folderLimit != null) body.folder_limit = params.folderLimit;
  if (params.folderIds != null && params.folderIds.length > 0) body.folder_ids = params.folderIds;
  if (params.includeUngrouped) body.include_ungrouped = true;

  const response = await fetch(`/api/v1/municipalities/${municipalityId}/file_library/search/exact`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "include",
    signal: params.signal,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await fileLibraryRequestError(response, `Failed to start exact search (${response.status})`);
  }

  return response.json();
}

export async function getExactSearch(
  municipalityId: number,
  searchId: number,
  params: { page?: number; perPage?: number; signal?: AbortSignal } = {},
): Promise<FileLibrarySearchResult> {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.perPage != null) searchParams.set("per_page", String(params.perPage));
  const query = searchParams.toString();

  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/file_library/search/exact/${searchId}${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      signal: params.signal,
    },
  );

  if (!response.ok) {
    throw await fileLibraryRequestError(response, `Failed to load exact search (${response.status})`);
  }

  return response.json();
}

function asComposeFolders(value: unknown): ExactSearchFolderChoice[] {
  if (!Array.isArray(value)) return [];
  const folders: ExactSearchFolderChoice[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const id = record.id;
    const name = record.name;
    if (typeof id !== "number" || !Number.isInteger(id) || id <= 0) continue;
    if (typeof name !== "string" || name.trim().length === 0) continue;
    folders.push({ id, name: name.trim() });
  }
  return folders;
}

function asComposeTerms(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

// Keep in sync with FileCenter::ExactSearchComposer::MAX_DESCRIPTION_LENGTH.
export const COMPOSE_DESCRIPTION_MAX_LENGTH = 2_000;

export async function composeExactSearchFields(
  municipalityId: number,
  description: string,
  signal?: AbortSignal,
): Promise<ExactSearchComposeResult> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/file_library/search/compose`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "include",
    signal,
    body: JSON.stringify({ description: description.trim() }),
  });

  if (!response.ok) {
    const data: unknown = await response.json().catch(() => ({}));
    const record = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
    const message =
      typeof record.error === "string" && record.error.trim().length > 0
        ? record.error
        : `Failed to compose search (${response.status})`;
    throw new Error(message);
  }

  const data: unknown = await response.json();
  if (!data || typeof data !== "object") {
    throw new Error("Failed to compose search");
  }
  const record = data as Record<string, unknown>;
  return {
    all: asComposeTerms(record.all),
    phrase: asComposeTerms(record.phrase),
    any: asComposeTerms(record.any),
    none: asComposeTerms(record.none),
    folders: asComposeFolders(record.folders),
  };
}

export async function getFileLibrary(municipalityId: number): Promise<FileLibraryPayload> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/file_library`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to load file library (${response.status})`);
  }

  return response.json();
}

// Lightweight counts only (no document list). Used by the LaunchPad dashboard so it does not
// download the full library just to display totals. See ENG-1422.
export async function getFileLibrarySummary(municipalityId: number): Promise<FileLibrarySummary> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/file_library?summary=true`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to load file library summary (${response.status})`);
  }

  return response.json();
}

// The folder tree with per-folder counts only (no documents). Loaded once for the File Center.
export async function getFileLibraryFolders(municipalityId: number): Promise<FileLibraryFolderTree> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/file_library/folders`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to load file library folders (${response.status})`);
  }

  return response.json();
}

// Paginated documents within a single folder (or the ungrouped bucket). Fetched lazily.
export async function getFolderDocuments(
  municipalityId: number,
  folderId: FolderTarget,
  params: GetFolderDocumentsParams = {},
): Promise<PaginatedFolderDocuments> {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.perPage != null) searchParams.set("per_page", String(params.perPage));
  const query = searchParams.toString();

  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/file_library/folders/${folderId}/documents${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      signal: params.signal,
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to load folder documents (${response.status})`);
  }

  return response.json();
}
